// Copyright ©️ 2025 eVolve MEP, LLC

import React from 'react';

import type {
  Facility,
  ShopConfiguration,
  WorkCell,
  ShopConfigurationId,
  WorkCellId,
  TaskTypeId,
  TaskTypeGroupId,
  WorkCellTaskType,
  WorkCellTaskTypeId,
} from '@evolvemep/api-ts';

import { useGeneralContext } from 'helpers/useGeneralContext';

import type { useWorkCellsGrid } from './useWorkCellsGrid';

export type WorkCellSetupContextType = {
  facility: Facility;
  /** `serverSideGridProps` specifically for the "Draft" table */
  serverSideGridProps: ReturnType<typeof useWorkCellsGrid>;
  /** The active, in use `shopConfiguration` */
  activeShopConfiguration: ShopConfiguration;
  draftShopConfiguration: ShopConfiguration | undefined;
  loadingDraftShopConfiguration: boolean;

  createDraftShopConfiguration: () => Promise<void>;
  deleteDraftShopConfiguration: () => Promise<void>;
  applyDraftShopConfiguration: () => Promise<void>;

  createWorkCell: (
    workCell: Pick<WorkCell, 'workCellName' | 'workCellDescription'>,
    shopConfigurationId: ShopConfigurationId,
  ) => Promise<WorkCell>;
  updateWorkCell: (params: Pick<WorkCell, 'workCellId' | 'workCellName' | 'workCellDescription'>) => Promise<WorkCell>;
  deleteWorkCell: (workCellId: WorkCellId) => Promise<void>;

  mapWorkCellTaskTypesAndGroups: (
    workCellId: WorkCellId,
    taskTypeIds: TaskTypeId[] | undefined,
    taskTypeGroupsIds: TaskTypeGroupId[] | undefined,
  ) => Promise<WorkCellTaskType[]>;
  unmapWorkCellTaskType: (workCellTaskTypeId: WorkCellTaskTypeId) => Promise<void>;

  mutationRunning: boolean;
};
export const WorkCellSetupContext = React.createContext<WorkCellSetupContextType | undefined>(undefined);
export const useWorkCellSetup = () => useGeneralContext(WorkCellSetupContext, 'WorkCellSetup');
